import { ChevronDown, ChevronUp, Minus, Plus, TicketPercent } from "lucide-react";
import { useEffect, useState } from "react";
import { useCart } from "../../../context/CartContext";
import axios from "axios";

const CartStep = ({ cart, onIncrease, onDecrease, totals }) => {

  const {
    applyCoupon,
    appliedCoupon,
    couponDiscount,
    removeCoupon,
  } = useCart();

  const [showCoupons, setShowCoupons] = useState(false);
  const [coupons, setCoupons] = useState([]);
  const [couponError, setCouponError] = useState("");
  const [applyingCode, setApplyingCode] = useState(null);
  const [couponLoading, setCouponLoading] = useState(false);

  const fetchCoupons = async () => {
    try {
      setCouponLoading(true);
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/coupons?admin=true`
      );
      setCoupons(res.data || []);
    } catch (err) {
      console.error("Error :", err)
    } finally {
      setCouponLoading(false);
    }
  };

  useEffect(() => {
    if (window.innerWidth < 1024) {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, []);

  return (
    <div className="space-y-6 w-full">
      <div className="space-y-2">
        {cart.map((item) => (
          <div
            key={`${item.serviceId}-${item?.brand}`}
            className="
            bg-white rounded-xl
             py-2 px-3 
            flex gap-4 shadow-lg
          "
          >
            {/* THUMBNAIL */}
            <img
              src={item.imageUrl}
              alt={item.title}
              className="size-20 rounded-lg object-cover flex-shrink-0"
            />

            {/* DETAILS */}
            <div className="flex-1 min-w-0 flex flex-col justify-between ">
              <div className="flex flex-col">
                <p className="font-semibold text-sm text-gray-900 truncate">
                  {item.title}
                </p>

                {item.brand && (
                  <p className="text-xs text-gray-500 truncate">{item.brand}</p>
                )}
              </div>

              <div className="flex justify-between items-center ">
                {/* QTY CONTROL */}
                <div className=" flex items-center justify-between border border-accentTextColor rounded-lg w-24 h-8 ">
                  <button
                    onClick={() => onDecrease(item)}
                    className=" flex-1 h-full flex items-center justify-center text-accentTextColor "
                  >
                    <Minus size={18} />
                  </button>

                  <span className="flex-1 text-center font-semibold text-primaryTextColor">
                    {item.qty}
                  </span>

                  <button
                    onClick={() => onIncrease(item)}
                    className="flex-1 h-full flex items-center justify-center text-accentTextColor"
                  >
                    <Plus size={18} />
                  </button>
                </div>

                {/* PRICE */}
                <div className="font-bold text-sm whitespace-nowrap flex flex-col justify-end">
                  <span className="text-end">
                    ₹{item.discountedPrice * item.qty}
                  </span>

                  {item.originalPrice > item.discountedPrice && (
                    <p className="text-xs text-green-600">
                      Saved ₹
                      {(item.originalPrice - item.discountedPrice) * item.qty}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      {/* APPLIED → NO CONTAINER */}
      {appliedCoupon ? (
        <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-xl px-3 py-2">
          <div className="flex items-center gap-2">
            <TicketPercent size={16} className="text-green-600" />
            <div className="leading-tight">
              <p className="text-sm font-semibold text-green-700">
                {appliedCoupon.code} applied
              </p>
              <p className="text-xs text-green-600 mt-1">
                You saved ₹{couponDiscount}
              </p>
            </div>
          </div>

          <button
            onClick={removeCoupon}
            className="text-[11px] font-semibold text-red-500 hover:underline"
          >
            Remove
          </button>
        </div>
      ) : (
        <div className="border rounded-xl overflow-hidden bg-white">
          {/* HEADER */}
          <button
            onClick={() => {
              setShowCoupons(!showCoupons);
              if (!coupons.length) fetchCoupons();
            }}
            className="w-full flex items-center justify-between px-3 py-3 bg-gray-50"
          >
            <div className="flex items-center gap-4">
              <div className="w-8 h-8 rounded-lg bg-primaryColor/10 flex items-center justify-center">
                <TicketPercent size={16} className="text-primaryColor" />
              </div>

              <div className="flex flex-col items-start leading-tight">
                <span className="text-sm font-semibold text-gray-800">
                  Apply offers & coupons
                </span>
                <span className="text-[11px] text-gray-500">
                  Save more on this order
                </span>
              </div>
            </div>

            {showCoupons ? (
              <ChevronUp size={18} className="text-gray-500" />
            ) : (
              <ChevronDown size={18} className="text-gray-500" />
            )}
          </button>

          {/* BODY */}
          {showCoupons && (
            <div className="px-3 py-2 space-y-2 border-t">

              {/* SKELETON */}
              {couponLoading &&
                Array.from({ length: 3 }).map((_, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between rounded-lg px-3 py-2 border animate-pulse"
                  >
                    <div className="flex flex-col gap-1 w-2/3">
                      <div className="h-3 w-24 bg-gray-200 rounded" />
                      <div className="h-2 w-32 bg-gray-100 rounded" />
                    </div>

                    <div className="h-6 w-14 bg-gray-200 rounded-md" />
                  </div>
                ))}

              {/* EMPTY STATE */}
              {!couponLoading && coupons.length === 0 && (
                <div className="flex items-center gap-2 px-2 py-2 text-xs text-gray-500">
                  <div className="w-7 h-7 rounded-md bg-gray-100 flex items-center justify-center">
                    <TicketPercent size={14} className="text-gray-400" />
                  </div>

                  <span>No coupons available right now</span>
                </div>
              )}

              {/* ACTUAL LIST */}
              {!couponLoading &&
                coupons.map((c) => {
                  const isDisabled =
                    totals.totalPrice < (c.minOrderAmount || 0);

                  return (
                    <div
                      key={c._id}
                      className={`flex items-center justify-between rounded-lg px-3 py-2 border transition
              ${isDisabled
                          ? "bg-gray-50 opacity-60"
                          : "bg-white hover:border-primaryColor/40"
                        }`}
                    >
                      <div className="flex flex-col leading-tight">
                        <span className="text-sm font-semibold text-gray-800">
                          {c.code}
                        </span>

                        <span className="text-[11px] text-gray-500">
                          {c.discountType === "FLAT"
                            ? `Flat ₹${c.discountValue} off`
                            : `${c.discountValue}% off`}
                        </span>

                        {isDisabled && (
                          <span className="text-[10px] text-red-500 mt-[2px]">
                            Min ₹{c.minOrderAmount}
                          </span>
                        )}
                      </div>

                      <button
                        onClick={async () => {
                          setApplyingCode(c.code);
                          setCouponError("");

                          const res = await applyCoupon({
                            code: c.code,
                          });

                          setApplyingCode(null);

                          if (!res.success) {
                            setCouponError(res.message);
                          } else {
                            setShowCoupons(false);
                          }
                        }}
                        disabled={isDisabled || applyingCode === c.code}
                        className={`
                text-xs font-semibold px-3 py-1 rounded-md transition
                ${isDisabled
                            ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                            : "bg-primaryColor/10 text-primaryColor hover:bg-primaryColor/20"
                          }
              `}
                      >
                        {applyingCode === c.code ? "..." : "Apply"}
                      </button>
                    </div>
                  );
                })}

              {/* ERROR */}
              {couponError && (
                <div className="text-[11px] text-red-500 bg-red-50 border border-red-200 rounded-md px-2 py-1">
                  {couponError}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* SUMMARY */}
      <div className="bg-gray-50 rounded-xl p-4 border text-gray-600 space-y-2">
        <div className="flex justify-between text-sm">
          <span>Subtotal</span>
          <span>₹{totals.subtotal}</span>
        </div>

        {totals.savings > 0 && (
          <div className="flex justify-between text-sm text-green-600">
            <span>Discount</span>
            <span>-₹{totals.savings}</span>
          </div>
        )}
        {couponDiscount > 0 && (
          <div className="flex justify-between text-sm text-green-600">
            <span>Coupon Discount</span>
            <span>-₹{couponDiscount}</span>
          </div>
        )}
        <div className="border-t pt-2 flex justify-between font-semibold">
          <span>Total</span>
          <span>₹{totals.totalPrice}</span>
        </div>
      </div>

      {!totals.isMinOrderReached && (
        <div className="mt-3 text-xs text-orange-600 bg-orange-50 border border-orange-200 rounded-lg p-2">
          Add services worth ₹{totals.remainingToMinOrder} more to proceed.
          <span className="block text-[0.65rem] text-gray-500 mt-0.5">
            Minimum order value is ₹{totals.minOrderAmount}
          </span>
        </div>
      )}
    </div>
  );
};

export default CartStep;
